import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { SpeechBubble } from '../components/ui/SpeechBubble';
import { FloatingDecor } from '../components/ui/FloatingDecor';
const sectionEntrance = {
  initial: {
    opacity: 0,
    y: 40,
    scale: 0.96
  },
  whileInView: {
    opacity: 1,
    y: 0,
    scale: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    type: 'spring',
    stiffness: 200,
    damping: 20
  }
};
const popWord = {
  initial: {
    opacity: 0,
    scale: 0.8,
    rotate: 0
  },
  animate: {
    opacity: 1,
    scale: 1,
    rotate: -2
  },
  transition: {
    type: 'spring',
    stiffness: 300,
    damping: 15,
    delay: 0.3
  }
};
export function About() {
  const values = [{
    badge: 'blue-palmtree' as const,
    title: 'Confiance',
    desc: "Des relations durables basées sur l'honnêteté et la transparence totale."
  }, {
    badge: 'rose-sparkle' as const,
    title: 'Créativité',
    desc: "Des concepts originaux qui sortent du lot et captivent l'audience."
  }, {
    badge: 'orange-ampersand' as const,
    title: 'Humain',
    desc: 'Le bien-être de nos talents et la satisfaction de nos clients avant tout.'
  }, {
    badge: 'yellow-star' as const,
    title: 'Impact Réel',
    desc: 'Des campagnes orientées résultats, au-delà des simples vanity metrics.'
  }];
  return <div className="pt-32 pb-0 overflow-hidden" data-id="element-99">
      {/* Story Section - Cream Background */}
      <section className="container mx-auto px-6 md:px-12 mb-24 relative" data-id="element-100">
        <FloatingDecor color="pink" count={6} data-id="element-101" />
        <div className="flex flex-col lg:flex-row gap-16 items-center relative z-10" data-id="element-102">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="flex-1 space-y-8" data-id="element-103">
            <SpeechBubble text="BEHIND THE SCENES" color="pink" data-id="element-104" />
            <h1 className="text-6xl md:text-8xl text-jany-dark" data-id="element-105">
              L'histoire de <br data-id="element-106" />
              <motion.span variants={popWord} initial="initial" animate="animate" className="inline-block text-jany-pink italic" data-id="element-107">
                Jany & Co
              </motion.span>
            </h1>
            <div className="space-y-6 text-xl text-jany-dark/80 font-medium" data-id="element-108">
              <p data-id="element-109">
                Fondée par Marine, Jany & Co est née d'une conviction simple :
                l'influence ne doit pas être une simple transaction, mais une
                véritable collaboration créative.
              </p>
              <p data-id="element-110">
                Après plusieurs années d'expérience dans le marketing digital et
                la gestion de talents, Marine a souhaité créer une agence à
                taille humaine, où la proximité, l'éthique et l'esthétique se
                rencontrent.
              </p>
              <p data-id="element-111">
                Aujourd'hui, Jany & Co est une agence pluridisciplinaire
                organisée autour de 4 expertises complémentaires : l'influence
                (gestion de talents et campagnes sponsorisées), la création de
                site web, la formation avec Jany Academy, et la production
                vidéo. Une seule signature, pour accompagner marques et
                créateurs de bout en bout, avec une touche méditerranéenne et
                chaleureuse qui fait notre signature.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="flex-1 w-full relative" data-id="element-112">
            <motion.div animate={{
            y: [0, -10, 0],
            rotate: [0, 5, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-10 -right-10 z-10" data-id="element-113">
              <Badge variant="yellow-star" size="xl" label="FOUNDER" data-id="element-114" />
            </motion.div>
            {/* Carte fondatrice : monogramme stylisé plutôt qu'une photo (pas de vraie photo de Marine disponible) */}
            <motion.div whileHover={{
            scale: 1.02,
            rotate: -1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 15
          }} className="relative aspect-[3/4] max-w-md mx-auto rounded-[4rem] overflow-hidden shadow-2xl border-8 border-white bg-gradient-to-br from-jany-pink-pale via-jany-cream to-jany-blue/30 flex flex-col items-center justify-center" data-id="element-115">
              <FloatingDecor color="pink" count={8} data-id="element-116" />
              <motion.div animate={{
              rotate: [0, 10, 0, -10, 0]
            }} transition={{
              repeat: Infinity,
              duration: 6,
              ease: 'easeInOut'
            }} className="relative z-10" data-id="element-117">
                <Sparkles className="text-jany-yellow mb-4 mx-auto" size={36} data-id="element-118" />
              </motion.div>
              <div className="relative z-10 w-32 h-32 rounded-full bg-white shadow-lg flex items-center justify-center mb-6" data-id="element-119">
                <span className="font-display text-6xl text-jany-pink" data-id="element-120">M</span>
              </div>
              <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-md p-8 rounded-[2rem] text-center shadow-lg" data-id="element-121">
                <h3 className="font-display text-4xl text-jany-pink mb-2" data-id="element-122">
                  Marine
                </h3>
                <p className="text-sm font-bold uppercase tracking-widest text-jany-orange" data-id="element-123">
                  Fondatrice & CEO
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section - Blue Background */}
      <motion.section {...sectionEntrance} className="bg-jany-blue py-32 rounded-t-[4rem] relative" data-id="element-124">
        <FloatingDecor color="dark" count={8} data-id="element-125" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-126">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-127">
            <SpeechBubble text="OUR VALUES" color="orange" className="mb-8" data-id="element-128" />
            <h2 className="text-5xl md:text-7xl mb-6 text-jany-dark" data-id="element-129">
              Ce qui nous anime
            </h2>
            <p className="text-2xl text-jany-dark/80 font-medium" data-id="element-130">
              Ce qui nous guide au quotidien dans chacune de nos collaborations.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-131">
            {values.map((value, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} whileHover={{
            y: -10,
            scale: 1.05,
            rotate: idx % 2 === 0 ? 2 : -2
          }} className="bg-white rounded-[3rem] p-10 text-center flex flex-col items-center shadow-lg border-4 border-white hover:border-jany-pink transition-colors" data-id="element-132">
                <Badge variant={value.badge} size="xl" className="mb-8" data-id="element-133" />
                <h3 className="text-3xl mb-4 font-display text-jany-dark" data-id="element-134">
                  {value.title}
                </h3>
                <p className="text-jany-dark/70 text-lg font-medium leading-relaxed" data-id="element-135">
                  {value.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </motion.section>
    </div>;
}